import {
  Button,
  DrawerBody,
  DrawerFooter,
  Input,
  Stack,
  Textarea,
} from "@assetwatch/ui";
import type { AssetId } from "../../../lib/workIds";
import type {
  SelectableRequestPriority,
  WorkRequestDraft,
} from "../../../types/workRequest";
import { type AiNotice, AiNoticeAlert } from "./AiNoticeAlert";
import type { StagedAttachment } from "./attachmentSelection";
import { NewRequestAttachments } from "./NewRequestAttachments";
import { PrioritySelector } from "./PrioritySelector";
import { RequestAssetPicker } from "./RequestAssetPicker";

type NewWorkRequestFormProps = {
  aiNotice: AiNotice | null;
  attachments: StagedAttachment[];
  attachmentErrors: string[];
  canReview: boolean;
  draft: WorkRequestDraft;
  suggestedPriority: SelectableRequestPriority | null;
  onAddAttachments: (files: File[]) => void;
  onAssetChange: (
    assetId: AssetId | null,
    assetName: string | null,
    assetKind: "ASSET" | "LOCATION" | null,
  ) => void;
  onDescriptionChange: (description: string) => void;
  onPriorityChange: (priority: SelectableRequestPriority | null) => void;
  onRemoveAttachment: (id: string) => void;
  onReview: () => void;
  onTitleChange: (title: string) => void;
};

export function NewWorkRequestForm({
  aiNotice,
  attachments,
  attachmentErrors,
  canReview,
  draft,
  suggestedPriority,
  onAddAttachments,
  onAssetChange,
  onDescriptionChange,
  onPriorityChange,
  onRemoveAttachment,
  onReview,
  onTitleChange,
}: NewWorkRequestFormProps) {
  return (
    <>
      <DrawerBody>
        <Stack gap={16}>
          {aiNotice ? <AiNoticeAlert notice={aiNotice} /> : null}
          <Input
            label="Title"
            maxLength={120}
            onChange={(event) => onTitleChange(event.target.value)}
            placeholder="Short summary"
            reserveSubtextSpace={false}
            value={draft.workRequestTitle ?? ""}
          />
          <Textarea
            label="Description"
            onChange={(event) => onDescriptionChange(event.target.value)}
            placeholder="Describe what you observed..."
            value={draft.workRequestDescription ?? ""}
          />
          <RequestAssetPicker onChange={onAssetChange} value={draft.assetId} />
          <PrioritySelector
            onChange={onPriorityChange}
            suggestedValue={suggestedPriority}
            value={draft.priorityCode}
          />
          <NewRequestAttachments
            errors={attachmentErrors}
            files={attachments}
            onAdd={onAddAttachments}
            onRemove={onRemoveAttachment}
          />
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        <Button disabled={!canReview} onClick={onReview}>
          Review request
        </Button>
      </DrawerFooter>
    </>
  );
}
